import { inject } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';

import { LoggingService } from '../logging/logging.service';

import { SSEService } from './sse.service';

export abstract class BaseSSEProgressService<T extends { id: string }> {
  protected readonly logger = inject(LoggingService);
  protected readonly sseService = inject(SSEService);

  // Generic BehaviorSubject for progress tracking
  public progress = new BehaviorSubject<T[] | null>(null);

  /**
   * Abstract method to hydrate progress data from SSE message.
   * Must be implemented by concrete services.
   *
   * @param data Raw progress data from SSE.
   * @param params Additional parameters specific to the service.
   *
   * @returns Hydrated progress object.
   */
  protected abstract hydrateProgressEvent(data: Partial<T>, ...params: unknown[]): T;

  /**
   * Abstract method to build the SSE topic URL.
   * Must be implemented by concrete services.
   *
   * @param params Parameters needed to build the topic.
   *
   * @returns The topic URL path.
   */
  protected abstract buildTopic(...params: unknown[]): string;

  /**
   * Abstract method to build the connection ID.
   * Must be implemented by concrete services.
   *
   * @param params Parameters needed to build the connection ID.
   *
   * @returns
   */
  protected abstract buildConnectionId(...params: unknown[]): string;

  /**
   * Watch progress for SSE updates.
   *
   * @param params Parameters needed for the specific service implementation.
   *
   * @returns Observable of progress updates.
   */
  protected watchSSEProgress(...params: unknown[]): Observable<T> {
    const topic = this.buildTopic(...params);
    const connectionId = this.buildConnectionId(...params);

    return this.sseService.watchTopic<T>(topic, connectionId).pipe(
      map(message => message.data),
      map(data => this.hydrateProgressEvent(data, ...params)),
      tap(progress => {
        this.updateProgress(progress);
        this.logger.debug('Progress update:', progress);
      })
    );
  }

  /**
   * Stop watching progress.
   *
   * @param params Parameters needed for the specific service implementation.
   */
  protected stopWatchingSSE(...params: unknown[]): void {
    const connectionId = this.buildConnectionId(...params);

    this.sseService.disconnect(connectionId);
  }

  /**
   * Updates a progress entry or adds a new one.
   *
   * @param progress The progress to update or add.
   */
  public updateProgress(progress: T): void {
    const currentProgress = this.progress.value || [];
    const progressId = progress.id;
    const index = currentProgress.findIndex(currentProgress => currentProgress.id === progressId);

    const newValue = [...currentProgress];

    if (index >= 0) {
      newValue[index] = progress;
    } else {
      newValue.push(progress);
    }

    this.progress.next(newValue);
  }

  /**
   * Removes a progress entry.
   *
   * @param id The ID of the progress to remove.
   */
  public removeProgress(id: string): void {
    const currentProgress = this.progress.value;

    if (!currentProgress) {
      return;
    }

    const filteredProgress = currentProgress.filter(currentProgress => currentProgress.id !== id) ?? null;

    this.progress.next(filteredProgress);
  }

  /**
   * Clear all progress entries.
   */
  public clearAllProgress(): void {
    this.progress.next(null);
  }
}
